'use client';

import { fetchMarketingBrandProductList } from '@/api/marketingPages';
import { useJump } from '@/components/MagicJump';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { MarketingProductBlockConfig } from '../../../types';
import {
  getMarketingBrandProductCelebrityImagePath,
  getMarketingBrandProductFeatureImagePath,
  getMarketingBrandProductGrayImagePath,
  getMarketingBrandProductImagePath,
  isMarketingBrandProductFeatureSlot,
  isMarketingBrandProductSoldOut,
} from '../brandProducts';
import {
  MARKETING_BRAND_PRODUCTS_SECTION_ID,
  getMarketingFlashSaleDisplayPrice,
  getMarketingFlashSaleProductHref,
  getMarketingFlashSaleProductName,
  getMarketingProductCardProduct,
  getStringValue,
  withS3Url,
  type MarketingProductItem,
} from './marketingAnniversaryUtils';
import MarketingBrandProductBaseCard from './MarketingBrandProductBaseCard';

interface MarketingBrandProductsSectionProps {
  brandProductsBlock: MarketingProductBlockConfig;
  marketingPageId: number;
  isPreview: boolean;
  isWap: boolean;
  cacheState?: MarketingBrandProductsCacheState;
  onCacheStateChange?: (state: MarketingBrandProductsCacheState) => void;
  onCollectClick?: (product: MarketingProductItem, index: number, collectType: string) => void;
  onProductExposure?: (product: MarketingProductItem, index: number) => void;
  onProductClick?: (product: MarketingProductItem, index: number) => void;
  onViewMoreClick?: () => void;
}

interface MarketingBrandProductsCacheState {
  pcProducts?: MarketingProductItem[];
  pcPage?: number;
  hasMoreProducts?: boolean;
  activeWapGroupIndex?: number;
}

interface MarketingBrandProductCardProps {
  product: MarketingProductItem;
  index: number;
  isWap: boolean;
  className?: string;
  onCollectClick?: (product: MarketingProductItem, index: number, collectType: string) => void;
  onProductExposure?: (product: MarketingProductItem, index: number) => void;
  onProductClick?: (product: MarketingProductItem, index: number) => void;
}

const MARKETING_BRAND_PRODUCTS_PC_PAGE_SIZE = 8;

function MarketingBrandProductCard({
  product,
  index,
  isWap,
  className = '',
  onCollectClick,
  onProductExposure,
  onProductClick,
}: MarketingBrandProductCardProps) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const toJumpTarget = useJump();
  const [isPcFeatureHovered, setIsPcFeatureHovered] = useState(false);
  const isFeatureCard = isMarketingBrandProductFeatureSlot(index, isWap);
  const isSoldOut = isMarketingBrandProductSoldOut(product);
  const celebrityImage = withS3Url(getMarketingBrandProductCelebrityImagePath(product));
  const grayImage = withS3Url(getMarketingBrandProductGrayImagePath(product));
  const productImageUrl = withS3Url(getMarketingBrandProductImagePath(product));
  const featureImage = withS3Url(getMarketingBrandProductFeatureImagePath(product));
  const image =
    isFeatureCard && (!isPcFeatureHovered || isWap)
      ? featureImage
      : grayImage || productImageUrl || featureImage;
  const isShowingCelebrityImage =
    isFeatureCard && Boolean(celebrityImage) && image === celebrityImage;
  const shouldInsetFeatureProductImage = isFeatureCard && !isWap && isPcFeatureHovered;
  const featureImageContainerClassName = shouldInsetFeatureProductImage
    ? 'absolute inset-y-0 left-[42px] right-[42px]'
    : 'absolute inset-0';
  const name = getMarketingFlashSaleProductName(product);
  const href = getMarketingFlashSaleProductHref(product);
  const { price, sellPrice } = getMarketingFlashSaleDisplayPrice(product, currency, userInfo);
  const formattedPrice = price ? formatPriceNumberWithSymbol(price, currency) : '';
  const formattedSellPrice = sellPrice
    ? formatPriceNumberWithSymbol(sellPrice, currency)
    : '$79.99';
  const shouldShowOriginalPrice = formattedPrice && formattedPrice !== formattedSellPrice;
  const { ref: featureExposureRef, inView: featureInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const featureExposureSentRef = useRef(false);

  useEffect(() => {
    if (!isFeatureCard || !featureInView || featureExposureSentRef.current) {
      return;
    }

    featureExposureSentRef.current = true;
    onProductExposure?.(product, index);
  }, [featureInView, index, isFeatureCard, onProductExposure, product]);

  const handleFeatureCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onProductClick?.(product, index);

    if (
      !href ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    toJumpTarget({
      jump_type: 4,
      Jump_target: href,
    });
  };

  const renderedProductImage = image ? (
    <div className={featureImageContainerClassName}>
      <Image
        src={image}
        alt=""
        fill
        sizes={isWap ? '50vw' : '25vw'}
        className={isShowingCelebrityImage ? 'object-cover' : 'object-contain'}
      />
    </div>
  ) : (
    <div className={featureImageContainerClassName}>
      <div className="relative h-full w-full overflow-hidden rounded-[50%] bg-[#eeeeee]">
        <div className="absolute left-[18%] top-[38%] h-[36%] w-[28%] rounded-[50%] border-[3px] border-[#5a2530]" />
        <div className="absolute right-[18%] top-[38%] h-[36%] w-[28%] rounded-[50%] border-[3px] border-[#5a2530]" />
        <div className="absolute left-[45%] top-[54%] h-[3px] w-[10%] bg-[#5a2530]" />
      </div>
    </div>
  );

  if (isFeatureCard) {
    return (
      <a
        ref={featureExposureRef}
        href={href || undefined}
        className={`relative block aspect-[408/493] min-w-0 max-w-full overflow-hidden rounded-[12px] bg-[#eeeeee] ${className}`}
        style={{ height: '100%', width: 'stretch' }}
        aria-label={name}
        onMouseEnter={isWap ? undefined : () => setIsPcFeatureHovered(true)}
        onMouseLeave={isWap ? undefined : () => setIsPcFeatureHovered(false)}
        onClick={handleFeatureCardClick}
      >
        {renderedProductImage}
        {isSoldOut && (
          <span
            className={`pointer-events-none absolute left-1/2 top-1/2 z-[3] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-center ${
              isWap ? 'size-[64px]' : 'size-[110px]'
            }`}
          >
            <span className="whitespace-pre-wrap text-[clamp(12px,0.885vw,17px)] leading-[clamp(14px,1.042vw,20px)] text-white">
              {tMarketing('flashSale.soldOut')}
            </span>
          </span>
        )}
        <div
          className={`absolute inset-x-0 bottom-0 box-border bg-gradient-to-t from-black/50 to-black/0 px-[16px] pt-[42px] ${
            isWap ? 'pb-[12px] text-center' : 'pb-[16px]'
          }`}
        >
          <div className="truncate font-br text-[14px] font-normal leading-[17px] text-white">
            {name}
          </div>
          <div
            className={`flex items-baseline gap-[6px] font-br text-white ${
              isWap ? 'mt-[6px] justify-center' : 'mt-[8px]'
            }`}
          >
            <span className="font-bb text-[16px] font-bold leading-[19px]">
              {formattedSellPrice}
            </span>
            {shouldShowOriginalPrice && (
              <span className="text-[14px] leading-[17px] line-through opacity-80">
                {formattedPrice}
              </span>
            )}
          </div>
        </div>
        {isSoldOut && (
          <div
            className={`pointer-events-none absolute inset-0 z-[1] rounded-[12px] ${
              isWap ? 'bg-white/50' : 'bg-black/50'
            }`}
          />
        )}
      </a>
    );
  }

  const productCardProduct = getMarketingProductCardProduct(product, productImageUrl || image);

  return (
    <MarketingBrandProductBaseCard
      product={productCardProduct}
      index={index}
      className={
        isWap
          ? `h-full min-w-0 overflow-hidden rounded-[6px] bg-white ${className}`
          : `h-full min-w-0 ${className}`
      }
      imageClassName={
        isWap
          ? '!h-[132.63px] !aspect-auto !rounded-[6px]'
          : '!aspect-square !min-h-0 !rounded-[12px]'
      }
      frameWidth={isWap ? '138.80453px' : '80%'}
      showTryOn={false}
      showReviewStar={false}
      showMember={false}
      showTag={false}
      showName={false}
      namePriceInline={false}
      showCorrelation={!isWap}
      showCorrelationCount={6}
      productImageWhiteBg
      collectScene="活动页品牌模块"
      loginSituation="活动页品牌模块"
      handelClick={() => onProductClick?.(product, index)}
      onInView={() => onProductExposure?.(product, index)}
      wished={() => onCollectClick?.(product, index, '收藏')}
      removed={() => onCollectClick?.(product, index, '取消收藏')}
    />
  );
}

export function MarketingBrandProductsSection({
  brandProductsBlock,
  marketingPageId,
  isPreview,
  isWap,
  cacheState,
  onCacheStateChange,
  onCollectClick,
  onProductExposure,
  onProductClick,
  onViewMoreClick,
}: MarketingBrandProductsSectionProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const [activeWapGroupIndex, setActiveWapGroupIndex] = useState(0);
  const wapSwiperRef = useRef<SwiperType | null>(null);
  const wapScrollbarRef = useRef<HTMLDivElement | null>(null);
  const activeWapGroupIndexRef = useRef(0);
  const wapProgressDragCleanupRef = useRef<(() => void) | null>(null);
  const hasHydratedCacheRef = useRef(false);
  const serverProducts = useMemo(
    () => brandProductsBlock.products.map((product) => product as MarketingProductItem),
    [brandProductsBlock.products],
  );
  const initialHasMoreProducts = Boolean(brandProductsBlock.product_template?.has_more);
  const [pcProducts, setPcProducts] = useState<MarketingProductItem[]>(serverProducts);
  const [pcPage, setPcPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(initialHasMoreProducts);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const allProducts = isWap ? serverProducts : pcProducts;
  const wapProductGroups = useMemo(
    () =>
      Array.from({ length: Math.ceil(allProducts.length / 4) }, (_, index) =>
        allProducts.slice(index * 4, index * 4 + 4),
      ),
    [allProducts],
  );
  const shouldLoopWapProducts = wapProductGroups.length > 1;
  const canToggleProductCount =
    !isWap && (hasMoreProducts || pcProducts.length > serverProducts.length);
  const getValidWapGroupIndex = useCallback(
    (index: unknown) => {
      const nextIndex = Number(index);

      if (!Number.isFinite(nextIndex) || wapProductGroups.length <= 0) {
        return 0;
      }

      return Math.min(Math.max(Math.floor(nextIndex), 0), wapProductGroups.length - 1);
    },
    [wapProductGroups.length],
  );
  const syncWapGroupIndex = useCallback(
    (
      index: unknown,
      options: {
        syncSwiper?: boolean;
        cache?: boolean;
      } = {},
    ) => {
      const nextIndex = getValidWapGroupIndex(index);

      activeWapGroupIndexRef.current = nextIndex;
      setActiveWapGroupIndex(nextIndex);

      if (options.syncSwiper) {
        if (shouldLoopWapProducts) {
          wapSwiperRef.current?.slideToLoop(nextIndex);
        } else {
          wapSwiperRef.current?.slideTo(nextIndex);
        }
      }

      if (options.cache) {
        onCacheStateChange?.({
          ...cacheState,
          activeWapGroupIndex: nextIndex,
        });
      }
    },
    [cacheState, getValidWapGroupIndex, onCacheStateChange, shouldLoopWapProducts],
  );

  useEffect(() => {
    if (hasHydratedCacheRef.current) {
      return;
    }

    setPcProducts(serverProducts);
    setPcPage(1);
    setHasMoreProducts(initialHasMoreProducts);
    setActiveWapGroupIndex(0);
    activeWapGroupIndexRef.current = 0;
  }, [serverProducts, initialHasMoreProducts]);

  useEffect(() => {
    if (!isWap || hasHydratedCacheRef.current || !cacheState) {
      return;
    }

    if (cacheState.activeWapGroupIndex === undefined) {
      return;
    }

    hasHydratedCacheRef.current = true;
    syncWapGroupIndex(cacheState.activeWapGroupIndex, { syncSwiper: true });
  }, [cacheState, isWap, syncWapGroupIndex]);

  useEffect(() => {
    if (isWap || hasHydratedCacheRef.current) {
      return;
    }

    if (!Array.isArray(cacheState?.pcProducts) || cacheState.pcProducts.length === 0) {
      return;
    }

    hasHydratedCacheRef.current = true;
    setPcProducts(cacheState.pcProducts);
    setPcPage(Number(cacheState.pcPage) || 1);
    setHasMoreProducts(Boolean(cacheState.hasMoreProducts));
  }, [cacheState, isWap]);

  useEffect(() => {
    return () => {
      wapProgressDragCleanupRef.current?.();
    };
  }, []);

  if (!brandProductsBlock.visible || allProducts.length === 0) {
    return null;
  }

  const renderBrandProductCard = (
    product: MarketingProductItem,
    index: number,
    slotClassName?: string,
  ) => (
    <div
      key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
      className={`min-w-0 ${slotClassName || ''}`}
    >
      <MarketingBrandProductCard
        product={product}
        index={index}
        isWap={isWap}
        onCollectClick={onCollectClick}
        onProductExposure={onProductExposure}
        onProductClick={onProductClick}
      />
    </div>
  );
  const renderWapProductGroup = (group: MarketingProductItem[], groupIndex: number) => {
    const startIndex = groupIndex * 4;
    const leftColumnItems = [
      group[0] ? renderBrandProductCard(group[0], startIndex, 'h-[240px]') : null,
      group[2] ? renderBrandProductCard(group[2], startIndex + 2, 'h-[180px]') : null,
    ].filter(Boolean);
    const rightColumnItems = [
      group[1] ? renderBrandProductCard(group[1], startIndex + 1, 'h-[180px]') : null,
      group[3] ? renderBrandProductCard(group[3], startIndex + 3, 'h-[240px]') : null,
    ].filter(Boolean);

    return (
      <div key={`brand-wap-group-${groupIndex}`} className="grid grid-cols-2 gap-[8px]">
        <div className="flex min-w-0 flex-col gap-[8px]">{leftColumnItems}</div>
        <div className="flex min-w-0 flex-col gap-[8px]">{rightColumnItems}</div>
      </div>
    );
  };
  const wapProgressWidth = wapProductGroups.length ? `${100 / wapProductGroups.length}%` : '100%';
  const wapProgressLeft = wapProductGroups.length
    ? `${(activeWapGroupIndex * 100) / wapProductGroups.length}%`
    : '0%';
  const viewMoreButtonClassName = isWap
    ? 'mx-auto mt-[18px] flex h-[38px] w-[264px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-white bg-transparent font-bb text-[14px] font-bold leading-[18px] text-white'
    : 'mx-auto mt-[40px] flex h-[48px] w-[340px] min-w-[128px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-[#808080] bg-transparent px-[46px] py-[10px] font-bb text-[14px] font-bold leading-[18px] text-[#282828]';
  const viewMoreButtonText =
    !hasMoreProducts && pcProducts.length > serverProducts.length
      ? tMarketing('common.showLess')
      : tMarketing('common.viewMore');
  const sectionTitle = brandProductsBlock.title || '';
  const sectionSubTitle = brandProductsBlock.sub_title || '';
  const handleViewMoreClick = async () => {
    if (isLoadingMore) {
      return;
    }

    onViewMoreClick?.();

    if (!hasMoreProducts && pcProducts.length > serverProducts.length) {
      setPcProducts(serverProducts);
      setPcPage(1);
      setHasMoreProducts(initialHasMoreProducts);
      onCacheStateChange?.({
        ...cacheState,
        pcProducts: serverProducts,
        pcPage: 1,
        hasMoreProducts: initialHasMoreProducts,
      });
      return;
    }

    if (!hasMoreProducts || !marketingPageId) {
      return;
    }

    const nextPage = pcPage + 1;
    setIsLoadingMore(true);
    try {
      const response = await fetchMarketingBrandProductList({
        marketingPageId,
        isPreview,
        page: nextPage,
        pageSize: MARKETING_BRAND_PRODUCTS_PC_PAGE_SIZE,
      });
      const nextProducts = (response?.items || []) as MarketingProductItem[];
      const mergedProducts =
        nextProducts.length > 0 ? [...pcProducts, ...nextProducts] : pcProducts;
      if (nextProducts.length > 0) {
        setPcProducts(mergedProducts);
      }
      setPcPage(nextPage);
      const nextHasMoreProducts = Boolean(response?.has_more);
      setHasMoreProducts(nextHasMoreProducts);
      onCacheStateChange?.({
        ...cacheState,
        pcProducts: mergedProducts,
        pcPage: nextPage,
        hasMoreProducts: nextHasMoreProducts,
      });
    } catch (error) {
      logger.error(error, 'Failed to fetch marketing brand products:');
    } finally {
      setIsLoadingMore(false);
    }
  };
  const viewMoreButton = (
    <button
      type="button"
      className={viewMoreButtonClassName}
      disabled={isLoadingMore}
      onClick={handleViewMoreClick}
    >
      {viewMoreButtonText}
    </button>
  );
  const getWapProgressIndex = (clientX: number) => {
    const scrollbar = wapScrollbarRef.current;
    if (!scrollbar || wapProductGroups.length <= 1) {
      return 0;
    }

    const rect = scrollbar.getBoundingClientRect();
    if (rect.width <= 0) {
      return activeWapGroupIndexRef.current;
    }

    const progress = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    return Math.round(progress * (wapProductGroups.length - 1));
  };
  const slideToWapProgress = (clientX: number) => {
    const nextIndex = getWapProgressIndex(clientX);
    if (nextIndex === activeWapGroupIndexRef.current) {
      return;
    }

    syncWapGroupIndex(nextIndex, { syncSwiper: true, cache: true });
  };
  const handleWapProgressPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    wapProgressDragCleanupRef.current?.();
    slideToWapProgress(event.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      slideToWapProgress(moveEvent.clientX);
    };
    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      wapProgressDragCleanupRef.current = null;
    };

    document.addEventListener('pointermove', handlePointerMove, { passive: false });
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    wapProgressDragCleanupRef.current = handlePointerUp;
  };

  return (
    <section
      id={MARKETING_BRAND_PRODUCTS_SECTION_ID}
      className={
        isWap
          ? 'relative z-10 my-[20px]'
          : 'relative z-10 mx-auto my-[20px] w-[calc(100%_-_96px)] max-w-[1680px] min-[1281px]:w-[calc(100%_-_240px)]'
      }
      style={
        brandProductsBlock.background_color
          ? { backgroundColor: brandProductsBlock.background_color }
          : undefined
      }
    >
      <div className={isWap ? 'pb-[20px] pt-[20px]' : 'pb-[40px] pt-[40px]'}>
        <div className={isWap ? 'mb-[16px] text-center' : 'mb-[24px] text-center'}>
          <h2
            className={`font-bb font-bold ${isWap ? 'text-white' : 'text-[#282828]'} ${
              isWap ? 'text-[20px] leading-[24px]' : 'text-[28px] leading-[34px]'
            }`}
          >
            {sectionTitle}
          </h2>
          {sectionSubTitle && (
            <p
              className={`mt-[4px] font-br font-normal ${isWap ? 'text-white' : 'text-[#808080]'} ${
                isWap ? 'text-[12px] leading-[14px] opacity-60' : 'text-[14px] leading-[17px]'
              }`}
            >
              {sectionSubTitle}
            </p>
          )}
        </div>
        {isWap ? (
          <div>
            <Swiper
              modules={shouldLoopWapProducts ? [Autoplay] : []}
              slidesPerView={1}
              spaceBetween={12}
              loop={shouldLoopWapProducts}
              autoplay={
                shouldLoopWapProducts
                  ? {
                      delay: 3000,
                      disableOnInteraction: false,
                    }
                  : false
              }
              onSwiper={(swiper) => {
                wapSwiperRef.current = swiper;
                const nextIndex =
                  cacheState?.activeWapGroupIndex === undefined
                    ? swiper.realIndex || 0
                    : cacheState.activeWapGroupIndex;
                syncWapGroupIndex(nextIndex, { syncSwiper: true });
              }}
              onSlideChange={(swiper) => {
                syncWapGroupIndex(swiper.realIndex || 0, { cache: true });
              }}
              className="!overflow-hidden"
            >
              {wapProductGroups.map((group, index) => (
                <SwiperSlide key={`brand-wap-slide-${index}`}>
                  <div className="px-[8px]">{renderWapProductGroup(group, index)}</div>
                </SwiperSlide>
              ))}
            </Swiper>
            {wapProductGroups.length > 1 && (
              <div
                ref={wapScrollbarRef}
                className="relative mx-auto mt-[12px] h-[4px] w-[208px] cursor-pointer touch-none rounded-[6px] bg-white/20"
                onPointerDown={handleWapProgressPointerDown}
              >
                <div
                  className="absolute top-0 h-full rounded-[6px] bg-white transition-[left] duration-300 ease-out"
                  style={{
                    left: wapProgressLeft,
                    width: wapProgressWidth,
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="grid auto-rows-fr grid-cols-4 items-stretch gap-x-[16px] gap-y-[40px]">
            {allProducts.map((product, index) => (
              <MarketingBrandProductCard
                key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
                product={product}
                index={index}
                isWap={isWap}
                onCollectClick={onCollectClick}
                onProductExposure={onProductExposure}
                onProductClick={onProductClick}
              />
            ))}
          </div>
        )}
        {!isWap && canToggleProductCount && viewMoreButton}
      </div>
    </section>
  );
}
